/**
 * @fileoverview System data-report builder — same three-pane shell as the tenant
 *             builder (activation + accordions | shared ReportCanvasBuilder |
 *             Live Preview / Properties) plus the system-library Library &
 *             Visibility accordion and AI-template sample preview.
 * @module system-web/pages/reports/SystemDataReportBuilderPage
 *
 * Responsibilities: - Own the outer shell + host data access for system-scoped
 *                     report definitions (the shared canvas/palette/schedule
 *                     manager are presentation-only; catalog, preview rows,
 *                     parameters, and schedules come from system services).
 *                   - Library & Visibility: isInLibrary toggle + department
 *                     visibility (all-departments / specific-tenants /
 *                     system-only) persisted via updateReportDefinition.
 *                   - AI-generated templates (schema-only, no tenant data):
 *                     render a clearly-labeled deterministic sample preview.
 * Dependencies:     - @firesquirrel/ui-admin (ReportCanvasBuilder,
 *                     ReportFieldPalette, ParameterEditor, ScheduleManager,
 *                     AccordionSection, DocumentLifecycleControls,
 *                     usePersistedNumber, columnActivationList).
 *                   - services/systemReportDefinitionService,
 *                     systemReportDataService, systemReportScheduleServicePort.
 * Architecture:     - Local-first: all reads/writes go through Dexie-backed
 *                     system services; the canvas never touches the API.
 * Notes:            - AI templates preview with deterministic sample rows
 *                     (system-web holds no tenant data); real department
 *                     preview data is a tracked follow-up (preview-department
 *                     infrastructure).
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import {
  ArrowLeft, Check, X, Columns3, PanelRightOpen, PanelRightClose,
  SlidersHorizontal, Library, CircleHelp,
} from 'lucide-react';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import {
  ReportCanvasBuilder, ReportFieldPalette, ParameterEditor, ScheduleManager, ReportBuilderHelpPanel,
  columnActivationList, AccordionSection, DocumentLifecycleControls,
  usePersistedNumber, DEFAULT_REPORT_CONFIG, REPORT_TOUR_TARGET,
} from '@firesquirrel/ui-admin';
import type { ReportCanvasChange, ReportFieldCatalogEntry } from '@firesquirrel/ui-admin';
import type { ReportParameter } from '@firesquirrel/core-domain';
import type { ReportDefinitionLocal, ReportConfig, ReportColumn } from '@firesquirrel/local-db';
import {
  getReportDefinition, updateReportDefinition, createReportDefinition,
  saveSystemReportParameters,
} from '../../services/systemReportDefinitionService';
import {
  getAvailableFields, fetchPreviewData, getCollectionCounts,
  getSchemaFieldsForAI, getSchemaPreviewForAI, type FieldDefinition,
} from '../../services/systemReportDataService';
import { systemScheduleServicePort } from '../../services/systemReportScheduleServicePort';

const REPORT_FORMATS: Array<{ value: ReportConfig['format']; label: string }> = [
  { value: 'tabular', label: 'Tabular' },
  { value: 'summary', label: 'Summary' },
  { value: 'form', label: 'Form-Style' },
  { value: 'crossTab', label: 'Cross-Tab' },
  { value: 'chartOnly', label: 'Chart' },
  { value: 'mapBased', label: 'Map' },
];

export default function SystemDataReportBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { setBreadcrumbLabel } = useBreadcrumbs();
  const isNew = !id || id === 'new';

  const aiTemplateData = location.state as { aiGenerated?: boolean; previewConfig?: any } | null;
  const [isAIGenerated, setIsAIGenerated] = useState<boolean>(aiTemplateData?.aiGenerated || false);

  const [report, setReport] = useState<ReportDefinitionLocal | null>(null);
  const [reportConfig, setReportConfig] = useState<ReportConfig>(DEFAULT_REPORT_CONFIG);
  const [reportName, setReportName] = useState('Untitled Report');
  const [reportDescription, setReportDescription] = useState('');
  const [parameters, setParameters] = useState<ReportParameter[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [helpOpen, setHelpOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<ReportColumn[]>([]);
  const [selectedColumnKey, setSelectedColumnKey] = useState<string | null>(null);
  const [isInLibrary, setIsInLibrary] = useState(false);
  const [visibility, setVisibility] = useState<ReportDefinitionLocal['visibility']>('all-departments');

  const [leftPanelWidth, setLeftPanelWidth] = usePersistedNumber('report-builder-panel-width', 256);
  const [previewWidth, setPreviewWidth] = usePersistedNumber('report-builder-preview-split', 384);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [rightTab, setRightTab] = useState<'preview' | 'properties'>('preview');
  const [propertiesHost, setPropertiesHost] = useState<HTMLDivElement | null>(null);
  const resizeRef = useRef<null | { kind: 'left' | 'preview'; startX: number; startWidth: number }>(null);

  const [previewRows, setPreviewRows] = useState<Record<string, any>[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (!isNew && id) {
      void loadReport(id);
    } else if (isNew && aiTemplateData?.previewConfig) {
      const config = aiTemplateData.previewConfig;
      setReportName(config.name || 'Untitled Report');
      setReportDescription(config.description || '');
      if (config.reportConfig) {
        setReportConfig(config.reportConfig);
        if (Array.isArray(config.reportConfig.columns)) {
          setSelectedColumns(config.reportConfig.columns);
        }
      }
      if (Array.isArray(config.parameters)) {
        setParameters(config.parameters);
      }
      setIsAIGenerated(true);
    }
  }, [id, isNew, aiTemplateData]);

  async function loadReport(reportId: string) {
    setLoading(true);
    try {
      const data = await getReportDefinition(reportId);
      if (data) {
        setReport(data);
        setReportName(data.name);
        setReportDescription(data.description || '');
        setReportConfig(data.reportConfig || DEFAULT_REPORT_CONFIG);
        setSelectedColumns(data.reportConfig?.columns || []);
        setParameters(data.parameters ?? []);
        setIsInLibrary(!!data.isInLibrary);
        setVisibility(data.visibility ?? 'all-departments');
        if (data.name) setBreadcrumbLabel(`/data-report-templates/${reportId}`, data.name);

        let isAITemplate = (data as any).aiGenerationSource === 'ai-generated';
        if (!isAITemplate && data.reportConfig?.dataSources) {
          const tenantCollections = ['inspectionSeries', 'cases', 'entities', 'violations', 'contacts', 'inspectionAccounts'];
          const hasTenantData = data.reportConfig.dataSources.some((ds: any) => tenantCollections.includes(ds.collection));
          if (hasTenantData) isAITemplate = true;
        }
        setIsAIGenerated(isAITemplate);
      }
    } catch (error) {
      console.error('[SystemDataReportBuilderPage] Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  }

  const beginResize = useCallback((kind: 'left' | 'preview', startWidth: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    resizeRef.current = { kind, startX: e.clientX, startWidth };
    const onMove = (ev: PointerEvent) => {
      const st = resizeRef.current;
      if (!st) return;
      const delta = st.kind === 'left' ? ev.clientX - st.startX : st.startX - ev.clientX;
      const raw = st.startWidth + delta;
      if (st.kind === 'left') {
        setLeftPanelWidth(Math.max(200, Math.min(440, raw)));
      } else if (raw < 200) {
        setPreviewCollapsed(true);
      } else {
        setPreviewCollapsed(false);
        setPreviewWidth(Math.max(260, Math.min(760, raw)));
      }
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [setLeftPanelWidth, setPreviewWidth]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'P' || e.key === 'p')) {
        e.preventDefault();
        setPreviewCollapsed((c) => !c);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleSelectColumn = useCallback((key: string | null) => {
    setSelectedColumnKey(key);
    if (key) {
      setRightTab('properties');
      setPreviewCollapsed(false);
    } else {
      setRightTab('preview');
    }
  }, []);

  // Preview rows — real Dexie data for manual templates, deterministic labeled
  // sample rows for AI (schema-only) templates.
  useEffect(() => {
    if (selectedColumns.length === 0) {
      setPreviewRows([]);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      setLoadingPreview(true);
      const columnFields = selectedColumns.filter((c) => c.visible !== false).map((c) => c.field);
      const promise = isAIGenerated
        ? getSchemaPreviewForAI(columnFields)
        : fetchPreviewData(columnFields, 50);
      promise.then((rows) => {
        if (!cancelled) setPreviewRows(rows);
      }).catch((err) => console.error('[SystemDataReportBuilderPage] preview fetch failed:', err))
        .finally(() => { if (!cancelled) setLoadingPreview(false); });
    }, 300);
    return () => { cancelled = true; window.clearTimeout(handle); };
  }, [selectedColumns, isAIGenerated]);

  const handleSave = useCallback(async (statusOverride?: ReportDefinitionLocal['status']) => {
    const status = statusOverride ?? report?.status ?? 'draft';
    const publishingNow = status === 'published';
    if (publishingNow) setPublishing(true); else setSaving(true);
    try {
      const reportData = {
        name: reportName || 'Untitled Report',
        description: reportDescription,
        type: 'report' as const,
        status,
        reportConfig: { ...reportConfig, columns: selectedColumns },
        parameters,
        parameterIds: parameters.map((p) => p.parameterId),
        scope: report?.scope || { level: 'system' as const },
        sharing: report?.sharing || { sharedWith: [{ type: 'all-below' as const, fromLevel: 'system' as const }], isReadOnly: false },
        isInLibrary,
        visibility,
        createdBy: report?.createdBy || 'system-admin',
        updatedBy: 'system-admin',
        ...(isAIGenerated && { aiGenerationSource: 'ai-generated' as const }),
      };

      if (isNew) {
        const newReport = await createReportDefinition(reportData as Partial<ReportDefinitionLocal>);
        navigate(`/data-report-templates/${newReport._id}`, { replace: true });
      } else if (id) {
        await updateReportDefinition(id, reportData);
        const refreshed = await getReportDefinition(id);
        if (refreshed) setReport(refreshed);
      }
    } catch (error) {
      console.error('[SystemDataReportBuilderPage] Failed to save report:', error);
    } finally {
      setSaving(false);
      setPublishing(false);
    }
  }, [report?.status, report?.scope, report?.sharing, report?.createdBy, reportName, reportDescription, reportConfig, selectedColumns, parameters, isInLibrary, visibility, isNew, id, navigate, isAIGenerated]);

  const handleCanvasChange = useCallback((patch: ReportCanvasChange) => {
    if (patch.columns) setSelectedColumns(patch.columns);
    if (patch.config) setReportConfig((prev) => ({ ...prev, ...patch.config }));
  }, []);

  const handleParametersChange = useCallback((next: ReportParameter[]) => {
    setParameters(next);
    if (id && !isNew) {
      void saveSystemReportParameters(id, next).catch((err) => {
        console.error('[SystemDataReportBuilderPage] Failed to save parameters:', err);
      });
    }
  }, [id, isNew]);

  const activationList = useMemo(
    () => columnActivationList(selectedColumns, selectedColumnKey),
    [selectedColumns, selectedColumnKey],
  );

  const loadCatalog = useCallback(async () => {
    if (isAIGenerated) {
      const fields = await getSchemaFieldsForAI();
      return { fields: fields as ReportFieldCatalogEntry[], counts: {} as Record<string, number> };
    }
    const [fields, counts] = await Promise.all([getAvailableFields(), getCollectionCounts()]);
    return { fields: fields as ReportFieldCatalogEntry[], counts };
  }, [isAIGenerated]);

  if (loading) {
    return <div className="flex h-full items-center justify-center"><div className="text-gray-500">Loading report...</div></div>;
  }

  const visibleColumns = selectedColumns.filter((c) => c.visible !== false);

  return (
    <div className="flex h-full flex-col" style={{ background: theme.pageBg }}>
      <header
        className="flex shrink-0 items-center justify-between border-b px-4 py-3"
        style={{ background: theme.headerBg, borderColor: theme.headerBorder }}
      >
        <div className="flex min-w-0 items-center gap-3">
          <button type="button" onClick={() => navigate('/data-report-templates')} aria-label="Back"
            className="rounded-lg p-2 hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="min-w-0">
            <input type="text" value={reportName} onChange={(e) => setReportName(e.target.value)}
              aria-label="Report name" placeholder="Report Name"
              className="rounded-sm border-none bg-transparent px-1 text-lg font-semibold focus:outline-hidden focus:ring-2 focus:ring-orange-500" />
            <input type="text" value={reportDescription} onChange={(e) => setReportDescription(e.target.value)}
              aria-label="Report description" placeholder="Add description..."
              className="block w-full rounded-sm border-none bg-transparent px-1 text-sm text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-orange-500" />
          </div>
          {isAIGenerated && (
            <span className="ml-2 rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-medium text-amber-700">AI Template</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            aria-label="Open report builder help"
            data-testid="report-builder-help-button"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <CircleHelp className="h-4 w-4" /> Help
          </button>
          <DocumentLifecycleControls
            status={report?.status}
            saving={saving}
            publishing={publishing}
            isNew={isNew}
            onSave={() => void handleSave()}
            onPublish={() => void handleSave('published')}
            onArchive={() => void handleSave('archived')}
            accentColor={theme.accent}
            saveDataTour={REPORT_TOUR_TARGET.savePublish}
          />
        </div>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside
          className="theme-grid shrink-0 overflow-y-auto border-r"
          style={{ width: leftPanelWidth, background: theme.toolbarBg, borderColor: theme.toolbarBorder }}
          data-testid="builder-left-panel"
          data-tour={REPORT_TOUR_TARGET.leftPanel}
        >
          <div className="border-b p-3" style={{ borderColor: theme.toolbarBorder }}>
            <div className="text-sm font-semibold text-gray-900">Report sections</div>
            <div className="text-xs text-gray-500">Check to show on the report · click to edit</div>
          </div>

          <div className="space-y-1 p-2" data-testid="activation-list">
            {activationList.map((entry) => {
              const col = selectedColumns[entry.index];
              return (
                <div
                  key={entry.field}
                  data-testid={`activation-row-${entry.field}`}
                  onClick={() => handleSelectColumn(entry.field)}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                    entry.active ? 'border-orange-200 bg-orange-50' : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <button
                    type="button"
                    aria-label={col?.visible !== false ? `Hide ${entry.label}` : `Show ${entry.label}`}
                    aria-pressed={col?.visible !== false}
                    data-testid={`activation-toggle-${entry.field}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedColumns((prev) => prev.map((c) =>
                        (c.columnId ?? c.field) === (col?.columnId ?? col?.field)
                          ? { ...c, visible: c.visible === false } : c));
                    }}
                    className={`rounded-sm p-1 ${col?.visible !== false ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                  >
                    {col?.visible !== false ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                  </button>
                  <Columns3 className={`h-4 w-4 ${col?.visible !== false ? 'text-gray-600' : 'text-gray-300'}`} />
                  <span className={`flex-1 text-sm ${col?.visible !== false ? 'text-gray-900' : 'text-gray-400'}`}>{entry.label}</span>
                </div>
              );
            })}
            {activationList.length === 0 && (
              <p className="px-3 py-4 text-center text-xs text-gray-400">No columns yet — add fields from the canvas palette.</p>
            )}
          </div>

          <div className="border-t p-2" style={{ borderColor: theme.toolbarBorder }}>
            <AccordionSection title="Report Format" storageKey="system-report-builder-section-format" defaultOpen>
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-gray-500">Format</span>
                <select aria-label="Report format" value={reportConfig.format}
                  onChange={(e) => setReportConfig((prev) => ({ ...prev, format: e.target.value as ReportConfig['format'] }))}
                  className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm">
                  {REPORT_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </label>
            </AccordionSection>

            <AccordionSection title="Parameters" storageKey="system-report-builder-section-parameters" icon={SlidersHorizontal} defaultOpen={searchParams.get('expand') === 'parameters'} forceOpen={searchParams.get('expand') === 'parameters'}>
              <div data-tour={REPORT_TOUR_TARGET.parameters}>
                <ParameterEditor parameters={parameters} onChange={handleParametersChange} />
              </div>
            </AccordionSection>

            {id && !isNew && (
              <AccordionSection title="Scheduled Delivery" storageKey="system-report-builder-section-schedule" defaultOpen={searchParams.get('expand') === 'schedule'} forceOpen={searchParams.get('expand') === 'schedule'}>
                <div data-tour={REPORT_TOUR_TARGET.schedule}>
                  <ScheduleManager
                    reportDefinitionId={id}
                    reportName={reportName}
                    parameters={parameters}
                    service={systemScheduleServicePort}
                    scopeLevel="system"
                  />
                </div>
              </AccordionSection>
            )}

            <AccordionSection title="Library & Visibility" storageKey="system-report-builder-section-library" icon={Library}>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" aria-label="Include in library" checked={isInLibrary}
                  onChange={(e) => setIsInLibrary(e.target.checked)} />
                Include in system report library
              </label>
              <label className="mt-2 block">
                <span className="mb-1 block text-[11px] font-medium text-gray-500">Visibility</span>
                <select aria-label="Visibility" value={visibility}
                  onChange={(e) => setVisibility(e.target.value as ReportDefinitionLocal['visibility'])}
                  className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm">
                  <option value="all-departments">All departments</option>
                  <option value="specific-tenants">Specific departments</option>
                  <option value="system-only">System only</option>
                </select>
              </label>
              {visibility === 'specific-tenants' && (
                <p className="mt-1 text-[11px] text-gray-400">
                  Department selection is managed from the report library grid after saving.
                </p>
              )}
            </AccordionSection>
          </div>
        </aside>

        <div role="separator" aria-orientation="vertical" aria-label="Resize settings panel"
          data-testid="left-panel-resizer" onPointerDown={beginResize('left', leftPanelWidth)}
          className="w-1 shrink-0 cursor-col-resize bg-transparent hover:bg-orange-300" />

        <main className="flex-1 overflow-auto p-6">
          <ReportCanvasBuilder
            reportConfig={reportConfig}
            columns={selectedColumns}
            parameters={parameters}
            reportName={reportName}
            reportDescription={reportDescription}
            selectedId={selectedColumnKey}
            onSelectedIdChange={handleSelectColumn}
            propertiesPortalTarget={propertiesHost}
            mode="edit"
            onChange={handleCanvasChange}
            renderPalette={({ onPick, selectedFields }) => (
              <ReportFieldPalette loadCatalog={loadCatalog} onPick={onPick} selectedFields={selectedFields}
                defaultExpanded={isAIGenerated ? undefined : ['Departments', 'Organizations']} />
            )}
          />

          {isAIGenerated && previewRows.length > 0 && (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-xs text-amber-800">
                <span className="font-semibold">Note:</span> This preview shows deterministic sample rows —
                system-web holds no tenant data for schema-based AI templates. Departments see their real data.
              </p>
            </div>
          )}
        </main>

        {!previewCollapsed && (
          <div role="separator" aria-orientation="vertical" aria-label="Resize preview"
            data-testid="preview-resizer" onPointerDown={beginResize('preview', previewWidth)}
            className="w-1 shrink-0 cursor-col-resize bg-transparent hover:bg-orange-300" />
        )}

        {previewCollapsed ? (
          <aside className="theme-grid flex w-10 shrink-0 flex-col items-center border-l pt-3"
            style={{ background: theme.toolbarBg, borderColor: theme.toolbarBorder }}
            data-testid="builder-preview-panel">
            <button type="button" aria-label="Expand pane" data-testid="expand-preview"
              onClick={() => setPreviewCollapsed(false)} title="Expand pane (Ctrl+Shift+P)"
              className="rounded-sm p-1 text-gray-500 hover:bg-gray-100">
              <PanelRightOpen className="h-4 w-4" />
            </button>
          </aside>
        ) : (
          <aside className="theme-grid flex shrink-0 flex-col border-l"
            style={{ width: previewWidth, background: theme.toolbarBg, borderColor: theme.toolbarBorder }}
            data-testid="builder-preview-panel" data-tour={REPORT_TOUR_TARGET.preview}>
            <div className="flex shrink-0 items-center gap-1 border-b border-gray-200 px-2 pt-1.5">
              <div role="tablist" aria-label="Preview and properties" className="flex flex-1 items-end gap-1">
                <button type="button" role="tab" aria-selected={rightTab === 'preview'}
                  data-testid="right-tab-preview" onClick={() => setRightTab('preview')}
                  className={`rounded-t px-2.5 py-1 text-xs font-medium ${rightTab === 'preview' ? 'border border-b-0 border-gray-200 bg-white text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}>
                  Live Preview
                </button>
                <button type="button" role="tab" aria-selected={rightTab === 'properties'}
                  data-testid="right-tab-properties" onClick={() => setRightTab('properties')}
                  className={`rounded-t px-2.5 py-1 text-xs font-medium ${rightTab === 'properties' ? 'border border-b-0 border-gray-200 bg-white text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}>
                  Properties
                </button>
              </div>
              <button type="button" aria-label="Collapse preview" data-testid="collapse-preview"
                onClick={() => setPreviewCollapsed(true)} title="Collapse pane (Ctrl+Shift+P)"
                className="mb-0.5 rounded-sm p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                <PanelRightClose className="h-4 w-4" />
              </button>
            </div>

            <div role="tabpanel" data-testid="right-tab-preview-panel"
              className={`flex-1 overflow-y-auto p-4 ${rightTab === 'preview' ? '' : 'hidden'}`}>
              <div className="mb-3 text-xs text-gray-500">
                {isAIGenerated ? 'Sample preview — schema-only AI template' : 'Live preview — reflects canvas edits against local data'}
              </div>
              {visibleColumns.length === 0 ? (
                <p className="text-xs text-gray-400">Add columns to preview the report.</p>
              ) : previewRows.length === 0 ? (
                <p className="text-xs text-gray-400">{loadingPreview ? 'Loading preview…' : 'No preview rows available.'}</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        {visibleColumns.map((col) => (
                          <th key={col.columnId ?? col.field} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {previewRows.map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          {visibleColumns.map((col) => (
                            <td key={col.columnId ?? col.field} className="px-4 py-3 text-sm text-gray-700">
                              {row[col.field] ?? '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div role="tabpanel" ref={setPropertiesHost} data-testid="properties-tab-host"
              className={`min-h-0 flex-1 ${rightTab === 'properties' ? 'flex flex-col' : 'hidden'}`}>
              {!selectedColumnKey && (
                <div className="flex h-full items-center justify-center p-6 text-center text-xs text-gray-400">
                  Select a column on the canvas to edit its properties.
                </div>
              )}
            </div>
          </aside>
        )}
      </div>

      <ReportBuilderHelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
